import { Field, FormId, PublicForm } from '@shared/types';
import { FunctionComponent, useMemo } from 'react';
import { usePublicCustomForm } from '../usePublicCustomForm';
import { CustomWebsiteField } from '../Fields/WebsiteField';
import { CustomEmailField } from '../Fields/EmailField';
import { Form } from '@cademy-ui/form';
import Button from '../../Button';
import { CustomNumberField } from '../Fields/NumberField';
import { CustomTextField } from '../Fields/TextField';
import { CustomRadioField } from '../Fields/RadioField';
import { CustomTextAreaField } from '../Fields/TextAreaField';
import Alert from '../../Alert';
import { CustomStatementField } from '../Fields/StatementField';
import { CustomAddressField } from '../Fields/AddressField';
import { CustomFirstNameField } from '../Fields/FirstNameField';
import { CustomLastNameField } from '../Fields/LastNameField';
import { CustomDropdownField } from '../Fields/DropdownField';
import { CustomDateField } from '../Fields/DateField';
import { CustomPhoneField } from '../Fields/TelephoneField';
import { CustomCheckboxGroupField } from '../Fields/CheckboxField';
import { CustomBooleanField } from '../Fields/BooleanField';

const DEFAULT_SUBMIT_LABEL = 'Submit';

const DEFAULT_REQUIRED_FIELDS = {
    system_first_name: true,
    system_last_name: true,
    system_email: true,
};

const itemsToRadioOptions = (items: string[]): Record<string, string> => {
    return items.reduce((options, item) => {
        return { ...options, ...{ [item]: item } };
    }, {});
};

type CustomFieldsProps = {
    form: PublicForm;
};
const CustomFields: FunctionComponent<CustomFieldsProps> = ({ form }) => {
    const validations = form.validations ?? [];
    const requiredFields = useMemo(
        () =>
            validations.reduce<Record<string, boolean>>((requiredIndex, validation) => {
                if (validation.condition === 'required') {
                    return { ...requiredIndex, ...{ [validation.field_ref]: validation.value } };
                }

                return requiredIndex;
            }, DEFAULT_REQUIRED_FIELDS),
        [form]
    );

    return form.fields.map((customField, index) => {
        const required = requiredFields[customField._id] ?? false;

        return <CustomFormField key={index} required={required} {...customField} />;
    });
};

type CustomFieldProps = Field & {
    fieldId?: string;
    disabled?: boolean;
    required?: boolean;
};

export const CustomFormField: FunctionComponent<CustomFieldProps> = ({
    fieldId,
    disabled = false,
    required = false,
    ...props
}) => {
    switch (props.data_type) {
        case 'url':
            return (
                <CustomWebsiteField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Website'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'address':
            return (
                <CustomAddressField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Address'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'boolean':
            return (
                <CustomBooleanField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Choose'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'checkbox':
            return (
                <CustomCheckboxGroupField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Choose'}
                    description={props.description}
                    options={props.items}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'date':
            return (
                <CustomDateField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Date'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'dropdown':
            return (
                <CustomDropdownField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Choose'}
                    description={props.description}
                    options={props.items}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'email':
            return (
                <CustomEmailField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Email'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'first_name':
            return (
                <CustomFirstNameField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'First Name'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'last_name':
            return (
                <CustomLastNameField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Last Name'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'number':
            return (
                <CustomNumberField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Number'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'radio':
            return (
                <CustomRadioField
                    id={fieldId}
                    options={itemsToRadioOptions(props.items)}
                    name={props._id}
                    label={props.label ?? 'Choose'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'tel':
            return (
                <CustomPhoneField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Telephone'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'text':
            return (
                <CustomTextField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Text'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'textarea':
            return (
                <CustomTextAreaField
                    id={fieldId}
                    name={props._id}
                    label={props.label ?? 'Text'}
                    description={props.description}
                    required={required}
                    disabled={disabled}
                />
            );

        case 'statement':
            return (
                <CustomStatementField
                    label={props.label ?? 'Statement'}
                    description={props.description}
                />
            );
    }
};

export type CustomFormProps = {
    formId: FormId;
    submitLabel?: string;
    onSubmit?: (values: Record<string, any>) => Promise<void>;
};

export const CustomForm: FunctionComponent<CustomFormProps> = ({
    formId,
    onSubmit,
    submitLabel = DEFAULT_SUBMIT_LABEL,
}) => {
    const { form, loading, errorMessage } = usePublicCustomForm({ formId });

    if (loading) {
        return null;
    }

    if (!form && errorMessage) {
        return <Alert preset="danger">{errorMessage}</Alert>;
    }

    if (!form) {
        return null;
    }

    return (
        <Form<Record<string, string>> onSubmit={onSubmit} requiredStrategy="required">
            {({ submit }) => {
                return (
                    <>
                        <CustomFields form={form} />
                        <Button onClick={submit} preset="primary">
                            {submitLabel}
                        </Button>
                    </>
                );
            }}
        </Form>
    );
};
